import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import businessService from '../services/business'
import { useLocations } from './LocationsContext'

const DashboardContext = createContext()

export const useDashboard = () => {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider')
  }
  return context
}

export const DashboardProvider = ({ children }) => {
  const { selectedLocationId } = useLocations()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)
  const [currentParams, setCurrentParams] = useState({
    location_id: null,
    period: '30d'
  })

  // Use refs to store current values without causing re-renders
  const dashboardDataRef = useRef(null)
  const currentParamsRef = useRef({
    location_id: null,
    period: '30d'
  })
  const lastFetchRef = useRef(null)
  const fetchingRef = useRef(false) // Prevent concurrent fetches

  // Update refs when state changes
  dashboardDataRef.current = dashboardData
  currentParamsRef.current = currentParams
  lastFetchRef.current = lastFetch

  // Cache duration in milliseconds (3 minutes)
  const CACHE_DURATION = 3 * 60 * 1000

  const shouldRefetch = useCallback((params) => {
    if (fetchingRef.current) return false // Already fetching
    if (!lastFetchRef.current || !dashboardDataRef.current) return true
    
    // Check if params changed
    if (JSON.stringify(params) !== JSON.stringify(currentParamsRef.current)) return true
    
    // Check if cache expired
    if (Date.now() - lastFetchRef.current > CACHE_DURATION) return true
    
    return false
  }, [])

  const fetchDashboardData = useCallback(async (params = {}) => {
    // Use selectedLocationId from context if not provided
    const fetchParams = {
      location_id: selectedLocationId,
      period: '30d',
      ...currentParamsRef.current,
      ...params
    }
    
    if (!shouldRefetch(fetchParams)) {
      return dashboardDataRef.current
    }

    // Prevent concurrent fetches
    if (fetchingRef.current) {
      return dashboardDataRef.current
    }

    fetchingRef.current = true
    setLoading(true)
    setError(null)

    try {
      const data = await businessService.getDashboardData(fetchParams)
      
      setDashboardData(data)
      setCurrentParams(fetchParams)
      setLastFetch(Date.now())
      return data
    } catch (err) {
      console.error('❌ Dashboard fetch error:', err)
      
      // More detailed error handling
      let errorMessage = 'Failed to load dashboard data'
      if (err.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.'
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. Please check your permissions.'
      } else if (err.response?.status === 404) {
        errorMessage = 'Dashboard endpoint not found.'
      } else if (err.message) {
        errorMessage = err.message
      }
      
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [shouldRefetch, selectedLocationId])

  const refreshDashboardData = useCallback(async (params = {}) => {
    setLastFetch(null) // Force refresh
    fetchingRef.current = false // Reset fetching flag
    return fetchDashboardData(params)
  }, [fetchDashboardData])

  // Auto-fetch when selectedLocationId changes
  useEffect(() => {
    if (selectedLocationId) {
      fetchDashboardData({ location_id: selectedLocationId })
    }
  }, [selectedLocationId, fetchDashboardData])

  const value = {
    dashboardData,
    loading,
    error,
    fetchDashboardData,
    refreshDashboardData,
    currentParams,
    lastFetch
  }

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  )
}

export default DashboardContext